/* eslint-disable max-lines */
import React, {
  Fragment,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import { useRecoilValue } from 'recoil';

import { ia, iaRa, snakeToTitleCase } from 'lib/helpers/utility';
import { useInfiniteFetchData } from 'lib/hooks/useInfiniteFetchData';
import useTimezone from 'lib/hooks/useTimezone';

import NotFound from 'components/practice/Dashboard/components/NotFound';
import { currentPractice } from 'components/practice/practiceState';
import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Button/Button';
import Icon from 'components/shared/Icon/Icon';
import ToggleSwitch from 'components/shared/ToggleSwitch/ToggleSwitch';
import Typography from 'components/shared/Typography/Typography';
import state from 'components/state';

import ActivitiesLogSkeleton from './ActivitiesLogSkeleton';
import ActivityLog from './components/ActivityLog';
import Comment from './components/Comment';
import CommentInput from './components/CommentInput';
import { exportActivitiesToPdf } from './lib/activityPdf/exportActivitiesToPdf';
import { expandActivityLogByIndex } from './lib/expandActivityLogByIndex';
import { formatChangedValues } from './lib/formatChangedValues';
import { formatDateHeader, groupActivitiesByDate } from './lib/groupActivitiesByDate';
import { useActivitiesEffects } from './useActivitiesEffects';
import { useComment } from './useComment';
import { useCommentInput } from './useCommentInput';

const ActivitiesLog = forwardRef((props, ref) => {
  const {
    id,
    fetchData,
    category,
    headerConfigs,
    customActivityFormatter = (activities) => activities,
    type,
    filterItems,
    extraParams = {},
    queryOptions = {}
  } = props;

  const scrollRef = useRef(null);
  const shouldScrollToBottom = useRef(true);
  const scrollAdjustmentRef = useRef(null);
  const userHasScrolled = useRef(false);
  const permission = useRecoilValue(state.permissions);
  const timezone = useTimezone();
  const practice = useRecoilValue(currentPractice);

  const [activeFilter, setActiveFilter] = useState('all');

  const handleFilterChange = (item) => {
    shouldScrollToBottom.current = true;
    userHasScrolled.current = false;
    setActiveFilter(item.value);
  };

  const filterType = activeFilter === 'all' ? type : activeFilter;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
    resetRefParams
  } = useInfiniteFetchData({
    queryKey: 'activities',
    queryFn: fetchData,
    params: { id, type: filterType, ...extraParams },
    dependencies: [category, id, filterType, JSON.stringify(extraParams)],
    options: { staleTime: Infinity, keepPreviousData: true, refetchOnMount: true, ...queryOptions }
  });

  const {
    isCreatingComment,
    onNewCommentPostHandler,
    onCommentDeleteHandler,
    onCommentSaveHandler
  } = useComment(category, id);

  const {
    commentInputContext,
    commentInputValue,
    focusInputTrigger,
    setCommentInputValue,
    onCancelContextHandler,
    openCommentInputHandler,
    onCommentSubmitHandler
  } = useCommentInput({
    onNewCommentPostHandler,
    onCommentSaveHandler,
    onBeforeCreate: () => {
      shouldScrollToBottom.current = true;
    }
  });

  const getFinalActivities = (propData = data) => {
    const expandedActivities = expandActivityLogByIndex(iaRa(propData)).sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );

    const customActivities = customActivityFormatter(expandedActivities, filterType);

    const formattedActivities = formatChangedValues({ activities: customActivities, timezone });

    const groupedActivities = groupActivitiesByDate(formattedActivities, timezone);

    return groupedActivities;
  };

  const finalGroupedActivities = useMemo(
    () => getFinalActivities(),
    [data, timezone, customActivityFormatter]
  );

  const handleExportPdf = useCallback(async () => {
    try {
      const newData = await refetch({ params: { forPdfExport: true } });
      resetRefParams();

      const groupedActivities = getFinalActivities(newData);

      const title = `${snakeToTitleCase(category) || ''} activity log`;

      await exportActivitiesToPdf({
        groupedActivities,
        title,
        category,
        headerConfigs: { ...headerConfigs, practice },
        isViewerAdmin: !!permission?.admin_id,
        timezone
      });
    } catch (error) {
      console.error('Failed to export an activity log to a PDF:', error);
      showAlert({ message: 'Failed to export an activity log to a PDF', color: 'danger' });
    }
  }, [refetch, category, practice, permission?.admin_id, headerConfigs, timezone]);

  useImperativeHandle(ref, () => ({ handleExportPdf }), [handleExportPdf]);

  useActivitiesEffects({
    scrollRef,
    shouldScrollToBottom,
    scrollAdjustmentRef,
    userHasScrolled,
    isLoading,
    data,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  });

  if (isLoading) return <ActivitiesLogSkeleton />;

  if (isError) {
    return (
      <NotFound
        className="justify-start pt-16"
        icon="illustration-no-content"
        title="Unable to load activities"
        description="Please try again later."
      />
    );
  }

  return (
    <>
      {ia(filterItems) && (
        <div className="w-full border-0 border-b border-solid border-b-neutral-100 bg-white px-4 py-2">
          <div className="max-w-full">
            <ToggleSwitch items={filterItems} active={activeFilter} onClick={handleFilterChange} />
          </div>
        </div>
      )}

      <div
        ref={scrollRef}
        className="-mt-[0.5px] flex flex-1 flex-col justify-between gap-5 overflow-y-auto px-5 pb-5">
        {isFetchingNextPage && (
          <div className="align-center flex justify-center pb-2">
            <Icon icon="loader-linear" className="animate-spin" color="primary" size={28} />
          </div>
        )}
        <div className="flex flex-col gap-4">
          {!ia(data) ? (
            <NotFound
              className="justify-start pt-16"
              icon="illustration-no-content"
              description="No activities yet"
              descriptionClassName="text-base text-neutral-900"
            />
          ) : (
            finalGroupedActivities.dateKeys.map((dateKey) => {
              const activities = finalGroupedActivities.grouped[dateKey];

              return (
                <div key={dateKey} className="flex flex-col">
                  <div className="sticky top-0 z-10 w-full border-b border-neutral-200 bg-white p-4 pl-0">
                    <Typography size="small" weight="semibold" color="neutral-800">
                      {formatDateHeader(activities[0].created_at, timezone)}
                    </Typography>
                  </div>

                  <div className="flex flex-col gap-5">
                    {iaRa(activities).map((activity, activityIndex) => {
                      return (
                        <Fragment key={activity.id || `${dateKey}-${activityIndex}`}>
                          {activity.type === 'comment' ? (
                            <Comment
                              comment={activity}
                              category={category}
                              refId={id}
                              onDelete={onCommentDeleteHandler}
                              openCommentInputHandler={openCommentInputHandler}
                            />
                          ) : (
                            <ActivityLog category={category} activity={activity} />
                          )}
                        </Fragment>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="flex w-full flex-col gap-1 border-0 border-t border-solid border-t-neutral-200 p-3">
        {commentInputContext?.fullName && (
          <div className="flex items-center justify-between px-1">
            <Typography size="small" weight="medium" color="neutral-800">
              {commentInputContext?.replying
                ? `Replying to ${commentInputContext.fullName}`
                : `Editing ${commentInputContext.isReply ? 'reply' : 'comment'}`}
            </Typography>

            <Button
              size="xs"
              variant="link"
              icon="close-linear"
              iconColor="neutral-700"
              onClick={onCancelContextHandler}
            />
          </div>
        )}

        <CommentInput
          onPost={onCommentSubmitHandler}
          loading={isCreatingComment}
          focusInputTrigger={focusInputTrigger}
          value={commentInputValue}
          onChange={setCommentInputValue}
          disableSend={
            !commentInputContext?.replying && commentInputContext?.value === commentInputValue
          }
          placeholder={
            commentInputContext
              ? commentInputContext.replying
                ? 'Reply'
                : `Edit ${commentInputContext.isReply ? 'reply' : 'comment'}`
              : 'Comment global'
          }
        />
      </div>
    </>
  );
});

ActivitiesLog.displayName = 'ActivitiesLog';

export default ActivitiesLog;
